import { useRef, useState, useEffect } from "react"
import { useParams, useNavigate, Link } from "@tanstack/react-router"
import { useQuery } from "@tanstack/react-query"
import { authClient } from "../lib/auth-client"
import { fetcher } from "../lib/api"
import { Skeleton, Button } from "../components/ui"
import { Shell } from "../components/shell"
import { CardDialog } from "../components/card-dialog"
import { BoardSettingsDialog } from "../components/board-settings-dialog"
import { NewCardDialog } from "../components/new-card-dialog"
import { useBoardSocket } from "../hooks/useBoardSocket"
import { ChevronLeft, ChevronRight, GanttChartSquare, Settings } from "lucide-react"
import { LIMITS, getPlan } from "../lib/plans"

const PRIORITY_COLORS: Record<string, string> = {
  urgent: "bg-red-500",
  high: "bg-orange-400",
  medium: "bg-yellow-500",
  low: "bg-green-500",
}


type CardItem = {
  id: string
  title: string
  priority: string | null
  startDate: string | null
  dueDate: string | null
  columnName: string
  boardId: string
  projectId: string
  assignees?: { user: { name: string | null; image: string | null } }[]
}

const DAY_W = 40 // px per day
const ROW_H = 44 // px per row
const LABEL_W = 220 // px for left label column

function toLocalDate(iso: string) {
  const d = new Date(iso)
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

function isoToLocal(iso: string) {
  const d = toLocalDate(iso)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
}

function daysBetween(a: Date, b: Date) {
  return Math.round((b.getTime() - a.getTime()) / 86400000)
}

function addDays(d: Date, n: number) {
  const r = new Date(d)
  r.setDate(r.getDate() + n)
  return r
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

/** Gantt-style timeline view of cards on a board. Shows start and end dates as bars on a horizontal timeline. */
export default function TimelinePage() {
  const { workspaceId, projectId, boardId } = useParams({
    from: "/workspaces/$workspaceId/projects/$projectId/boards/$boardId/timeline",
  })
  const navigate = useNavigate()
  const { data: session } = authClient.useSession()
  const scrollRef = useRef<HTMLDivElement>(null)

  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const [offsetDays, setOffsetDays] = useState(-14) // days from today to left edge
  const [openCardId, setOpenCardId] = useState<string | null>(null)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [newCardOpen, setNewCardOpen] = useState(false)
  useBoardSocket(boardId)

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || (e.target as HTMLElement).isContentEditable) return
      if (e.key === "Escape") { setNewCardOpen(false); setOpenCardId(null); setSettingsOpen(false) }
      if (e.key === "/") { e.preventDefault(); document.querySelector<HTMLInputElement>("[data-global-search]")?.focus() }
      if (e.key === "n" || e.key === "N") { e.preventDefault(); setNewCardOpen(true) }
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [])
  const VISIBLE_DAYS = 90

  const { data: workspace } = useQuery({
    queryKey: ["workspace", workspaceId],
    queryFn: () => fetcher<{ role: string; plan: string }>(`/api/workspaces/${workspaceId}`),
    enabled: !!session,
  })
  const canTimeline = LIMITS[getPlan(workspace?.plan)].timeline
  const isAdmin = workspace?.role === "owner" || workspace?.role === "admin"

  const { data: board, isLoading } = useQuery({
    queryKey: ["board", boardId],
    queryFn: () =>
      fetcher<{ name: string; color: string | null; projectId: string; columns: { id: string; name: string; cards: any[] }[] }>(
        `/api/boards/${boardId}`
      ),
    enabled: !!session,
  })

  if (!session) { navigate({ to: "/login" }); return null }
  if (workspace && !canTimeline) {
    navigate({ to: "/workspaces/$workspaceId/billing", params: { workspaceId } })
    return null
  }

  const cards: CardItem[] = board?.columns.flatMap((col) =>
    col.cards
      .filter((c) => c.startDate || c.dueDate)
      .map((c) => ({
        id: c.id,
        title: c.title,
        priority: c.priority,
        startDate: c.startDate,
        dueDate: c.dueDate,
        columnName: col.name,
        boardId,
        projectId,
        assignees: c.assignees,
      }))
  ) ?? []

  const viewStart = addDays(today, offsetDays)
  // Build month header groups
  const monthGroups: { label: string; startDay: number; days: number }[] = []
  for (let i = 0; i < VISIBLE_DAYS; i++) {
    const d = addDays(viewStart, i)
    const label = `${MONTHS[d.getMonth()]} ${d.getFullYear()}`
    if (monthGroups.length === 0 || monthGroups[monthGroups.length - 1].label !== label) {
      monthGroups.push({ label, startDay: i, days: 1 })
    } else {
      monthGroups[monthGroups.length - 1].days++
    }
  }

  const todayOffset = daysBetween(viewStart, today)

  function xForDay(day: number) { return day * DAY_W }

  const pan = (dir: number) => setOffsetDays((o) => o + dir * 14)
  const jumpToday = () => setOffsetDays(-14)

  const exportCSV = () => {
    const rows: string[][] = [["Title", "Column", "Priority", "Start Date", "Due Date"]]
    for (const card of cards) {
      rows.push([card.title, card.columnName, card.priority || "", card.startDate ? new Date(card.startDate).toLocaleDateString() : "", card.dueDate ? new Date(card.dueDate).toLocaleDateString() : ""])
    }
    const csv = rows.map((r) => r.map((v) => `"${v.replace(/"/g, '""')}"`).join(",")).join("\n")
    const blob = new Blob([csv], { type: "text/csv" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url; a.download = `${board?.name || "timeline"}.csv`; a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <Shell
      title={board?.name || "Timeline"}
      back={{ to: "/workspaces/$workspaceId/projects/$projectId", label: "Project" }}
      boardColor={board?.color}
      actions={
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={exportCSV} title="Export as CSV">Export CSV</Button>
          {isAdmin && (
            <Button size="sm" variant="outline" onClick={() => setSettingsOpen(true)}><Settings className="mr-1 h-4 w-4" />Settings</Button>
          )}
        </div>
      }
      tabs={
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 text-sm">
            <Link
              to="/workspaces/$workspaceId/projects/$projectId/boards/$boardId"
              params={{ workspaceId, projectId, boardId }}
              className="rounded-md px-3 py-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              Board
            </Link>
            <Link
              to="/workspaces/$workspaceId/projects/$projectId/boards/$boardId/list"
              params={{ workspaceId, projectId, boardId }}
              className="rounded-md px-3 py-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              List
            </Link>
            <Link
              to="/workspaces/$workspaceId/projects/$projectId/boards/$boardId/calendar"
              params={{ workspaceId, projectId, boardId }}
              className="rounded-md px-3 py-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              Calendar
            </Link>
            <span className="rounded-md bg-primary/10 px-3 py-1.5 font-medium text-primary">Timeline</span>
          </div>
          <span className="hidden items-center gap-2 text-xs text-muted-foreground sm:flex">
            <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px]">N</kbd> new card
            <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px]">/</kbd> search
            <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px]">Esc</kbd> close
          </span>
          <div className="flex items-center gap-2">
            <button onClick={() => pan(-1)} className="rounded-md p-1.5 hover:bg-muted">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              onClick={jumpToday}
              className="rounded-md border border-border px-2.5 py-1 text-xs font-medium hover:bg-muted"
            >
              Today
            </button>
            <button onClick={() => pan(1)} className="rounded-md p-1.5 hover:bg-muted">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      }
    >
      <main className="flex flex-1 flex-col overflow-hidden p-4" style={board?.color ? { background: board.color } : undefined}>
        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-10 rounded-lg" />)}
          </div>
        ) : cards.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center">
            <GanttChartSquare className="h-12 w-12 text-muted-foreground/30" />
            <p className="text-sm font-medium text-muted-foreground">No cards with dates</p>
            <p className="text-xs text-muted-foreground">
              Add a start date or due date to cards in the Board view to see them here.
            </p>
          </div>
        ) : (
          <div className="flex flex-1 overflow-hidden rounded-xl border border-border bg-card shadow-sm">
            {/* Left label column */}
            <div className="flex flex-col border-r border-border" style={{ width: LABEL_W, minWidth: LABEL_W }}>
              {/* Header spacer row */}
              <div className="flex h-[52px] items-end border-b border-border px-3 pb-1">
                <span className="text-xs font-medium text-muted-foreground">Card</span>
              </div>
              {cards.map((card) => (
                <button
                  key={card.id}
                  onClick={() => setOpenCardId(card.id)}
                  className="flex w-full items-center gap-2 border-b border-border px-3 hover:bg-muted/50 transition-colors text-left"
                  style={{ height: ROW_H }}
                >
                  {card.priority && (
                    <span className={`h-2 w-2 shrink-0 rounded-full ${PRIORITY_COLORS[card.priority] ?? "bg-muted-foreground"}`} />
                  )}
                  <span className="truncate text-xs font-medium">{card.title}</span>
                </button>
              ))}
            </div>

            {/* Scrollable Gantt area */}
            <div className="flex flex-1 flex-col overflow-auto" ref={scrollRef}>
              <div style={{ minWidth: VISIBLE_DAYS * DAY_W }}>
                {/* Month header */}
                <div className="flex border-b border-border bg-muted/30" style={{ height: 28 }}>
                  {monthGroups.map((mg) => (
                    <div
                      key={mg.label + mg.startDay}
                      className="border-r border-border px-2 text-[11px] font-semibold text-muted-foreground flex items-center"
                      style={{ width: mg.days * DAY_W, minWidth: mg.days * DAY_W }}
                    >
                      {mg.label}
                    </div>
                  ))}
                </div>

                {/* Day header */}
                <div className="flex border-b border-border" style={{ height: 24 }}>
                  {Array.from({ length: VISIBLE_DAYS }).map((_, i) => {
                    const d = addDays(viewStart, i)
                    const isToday = i === todayOffset
                    const isWeekend = d.getDay() === 0 || d.getDay() === 6
                    return (
                      <div
                        key={i}
                        className={`flex items-center justify-center border-r border-border text-[10px] font-medium
                          ${isToday ? "bg-primary text-primary-foreground" : isWeekend ? "text-muted-foreground/50" : "text-muted-foreground"}`}
                        style={{ width: DAY_W, minWidth: DAY_W }}
                      >
                        {d.getDate()}
                      </div>
                    )
                  })}
                </div>

                {/* Rows */}
                <div className="relative">
                  {/* Today vertical line */}
                  {todayOffset >= 0 && todayOffset < VISIBLE_DAYS && (
                    <div
                      className="absolute top-0 bottom-0 w-px bg-primary/40 z-10 pointer-events-none"
                      style={{ left: xForDay(todayOffset) + DAY_W / 2 }}
                    />
                  )}

                  {cards.map((card) => {
                    const start = card.startDate ? toLocalDate(card.startDate) : card.dueDate ? toLocalDate(card.dueDate) : null
                    const end = card.dueDate ? toLocalDate(card.dueDate) : card.startDate ? toLocalDate(card.startDate) : null
                    if (!start || !end) return null

                    const startOff = daysBetween(viewStart, start)
                    const endOff = daysBetween(viewStart, end)
                    const barStart = Math.max(0, startOff)
                    const barEnd = Math.min(VISIBLE_DAYS - 1, endOff)
                    const barWidth = Math.max(1, barEnd - barStart + 1) * DAY_W - 4
                    const barLeft = barStart * DAY_W + 2
                    const isOutOfView = endOff < 0 || startOff >= VISIBLE_DAYS
                    const color = PRIORITY_COLORS[card.priority ?? "medium"] ?? "bg-blue-500"

                    return (
                      <div
                        key={card.id}
                        className="relative border-b border-border"
                        style={{ height: ROW_H }}
                      >
                        {/* Weekend shading */}
                        {Array.from({ length: VISIBLE_DAYS }).map((_, i) => {
                          const d = addDays(viewStart, i)
                          if (d.getDay() !== 0 && d.getDay() !== 6) return null
                          return (
                            <div
                              key={i}
                              className="absolute top-0 bottom-0 bg-muted/30"
                              style={{ left: i * DAY_W, width: DAY_W }}
                            />
                          )
                        })}

                        {!isOutOfView && (
                          <button
                            onClick={() => setOpenCardId(card.id)}
                            className={`absolute top-2 flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium text-white shadow-sm hover:brightness-110 transition-all cursor-pointer ${color}`}
                            style={{ left: barLeft, width: barWidth, overflow: "hidden" }}
                            title={`${card.title} (${isoToLocal(card.startDate ?? card.dueDate!)} → ${isoToLocal(card.dueDate ?? card.startDate!)})`}
                          >
                            <span className="truncate leading-none">{card.title}</span>
                          </button>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Legend */}
        <div className="mt-3 flex flex-wrap items-center gap-4 rounded-md border border-border bg-card/80 p-2 text-xs text-muted-foreground backdrop-blur-sm">
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-red-500" /> Urgent</span>
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-orange-400" /> High</span>
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-yellow-500" /> Medium</span>
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-green-500" /> Low</span>
          <span className="ml-auto flex items-center gap-1.5 text-muted-foreground/80">
            Set start & due dates on cards to display them here
          </span>
        </div>
      </main>
      <CardDialog
        open={!!openCardId}
        onClose={() => setOpenCardId(null)}
        cardId={openCardId}
        boardId={boardId}
        workspaceId={workspaceId}
      />
      <BoardSettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        boardId={boardId}
        workspaceId={workspaceId}
      />
      <NewCardDialog
        open={newCardOpen}
        onClose={() => setNewCardOpen(false)}
        boardId={boardId}
      />
    </Shell>
  )
}
